import type { Ref } from 'vue'
import debounce from '@/js/helpers/debounce'

type ProductPageScrollConfig = {
  rhpaContent: Ref<HTMLElement | null>,
  mediaContent: Ref<HTMLElement | null>,
  mediaContainer: Ref<HTMLElement | null>
  fitMediaToViewport?: boolean
}

export function setupScrollListener(config: ProductPageScrollConfig) {
  const { rhpaContent, mediaContent, mediaContainer, fitMediaToViewport } = config

  let buttonPosition = 0

  const isMobile = () => window.innerWidth <= 1023

  function updateButtonPosition() {

    if (!rhpaContent.value) {      
      return
    }

    if (mediaContainer.value) {
      mediaContainer.value.style.height = ''
    }

    // Button is the element at the bottom of the rhpa. Use the add to wishlist first, since that's always at the bottom.
    let button = rhpaContent.value.querySelector('.rhpa-bottom .wishlist') as HTMLElement | null

    // If the add to wishlist is not present, use the last add to cart button that is present
    if (!button) {
      const a2cButtons = Array.from(rhpaContent.value.querySelectorAll('.rhpa-bottom .a2c')) as HTMLElement[]
      button = a2cButtons.length ? a2cButtons[a2cButtons.length - 1] : null
    }

    if (!button) {      
      return
    }

    let currentElement: HTMLElement | null = button
    let totalOffset = 0

    while (currentElement) {
      totalOffset += currentElement.offsetTop
      currentElement = currentElement.offsetParent as HTMLElement
    }

    buttonPosition = totalOffset

    // On desktop, the rhpa should stick once it has scrolled all the way down
    if (!isMobile()) {
      rhpaContent.value.style.top = `-${rhpaContent.value.clientHeight - window.innerHeight}px`
    }
  }

  // Desktop height and position calculation
  function update() {
    if (!mediaContent.value || !mediaContainer.value) {      
      return
    }

    // Mobile: once the media area scrolls past the Add to Cart button, we hide the media area
    if (isMobile()) {
      const visible = window.scrollY <= buttonPosition
      mediaContainer.value.style.top = visible ? '0' : `-${mediaContainer.value.clientHeight + 200}px`

      return
    }

    // A media area fitted to the viewport keeps its own height and scrolls away with the page instead of sticking beside the RHPA.
    if (buttonPosition > window.innerHeight && !fitMediaToViewport) {
      // Desktop: we compute a height for the media area that keeps it on the page for as long as there is RHPA left to scroll
      const newHeight = `${buttonPosition}px`
      const newTop = `${Math.max(0, (window.innerHeight - mediaContent.value.clientHeight) / 2)}px`
      
      mediaContainer.value.style.height = newHeight
      mediaContent.value.style.top = newTop
    } else {
      mediaContent.value.style.top = ''
      mediaContainer.value.style.height = ''
    }
  }

  // Scroll listener management
  let scrollListener: (() => void) | null = null

  // Mode management and resize handling
  const handleResize = () => {
    updateButtonPosition()
    const mobile = isMobile()

    if (mediaContainer.value) {
      if (mobile) {
        // Make sure that on Mobile, we aren't setting the media content height (used only for Desktop)
        mediaContainer.value.style.height = 'auto'
      } else {
        // On Desktop, we aren't setting the media content height (used only for Desktop)
        mediaContainer.value.style.top = ''
      }
    }

    if (mobile && !scrollListener) {
      scrollListener = update
      window.addEventListener('scroll', scrollListener)
    } else if (!mobile && scrollListener) {
      window.removeEventListener('scroll', scrollListener)
      scrollListener = null
    }

    update()
  }

  // Initial setup — suppress CSS transitions so the media panel doesn't
  // animate from its default values to the calculated position on page load.
  const elementsToSuppress = [mediaContent.value, mediaContainer.value].filter(Boolean) as HTMLElement[]
  elementsToSuppress.forEach(el => el.style.transition = 'none')
  handleResize()
  // Re-enable transitions after the initial values are applied
  requestAnimationFrame(() => {
    elementsToSuppress.forEach(el => el.style.transition = '')
  })

  // Event listeners with debouncing
  window.addEventListener('resize', handleResize)

  const setupMutationObservers = () => {
    let lastRhpaHeight = rhpaContent.value?.clientHeight || 0
    let lastButtonCount = rhpaContent.value?.querySelectorAll('.rhpa-bottom .a2c, .rhpa-bottom .wishlist').length || 0

    function mutationCallback() {
      // Check if the layout actually changed (height or button count)
      const currentHeight = rhpaContent.value?.clientHeight || 0
      const currentButtonCount = rhpaContent.value?.querySelectorAll('.rhpa-bottom .a2c, .rhpa-bottom .wishlist').length || 0

      if (currentHeight !== lastRhpaHeight || currentButtonCount !== lastButtonCount) {
        lastRhpaHeight = currentHeight
        lastButtonCount = currentButtonCount

        if (!isMobile() && rhpaContent.value) {
          // Measure position before recalculating style.top so we can detect any
          // sticky-snap jump and cancel it out in the same JS tick (before repaint).
          const topBefore = rhpaContent.value.getBoundingClientRect().top
          updateButtonPosition()
          const topAfter = rhpaContent.value.getBoundingClientRect().top
          const jump = topAfter - topBefore
          if (jump !== 0) {
            window.scrollBy(0, jump)
          }
        } else {
          updateButtonPosition()
        }
      }

      // On desktop: update media container height to match the new RHPA length.
      // On mobile: skip — let the scroll listener handle visibility so accordion
      // height changes don't cause the media to flash in/out of view.
      if (!isMobile()) {
        update()
      }
    }

    const debouncedCallback = debounce(mutationCallback, 100)

    // rhpaContent: watch DOM changes AND class attribute changes (accordion toggles)
    const rhpaObserver = new MutationObserver(debouncedCallback)
    if (rhpaContent.value) {
      rhpaObserver.observe(rhpaContent.value, {
        childList       : true,
        subtree         : true,
        attributes      : true,
        attributeFilter : ['class'], // the observer now also fires whenever any element in the subtree gets a class change.
        characterData   : false,
      })
    }

    // mediaContent: only needs DOM structure changes
    const mediaObserver = new MutationObserver(debouncedCallback)
    if (mediaContent.value) {
      mediaObserver.observe(mediaContent.value, {
        childList     : true,
        subtree       : true,
        attributes    : false,
        characterData : false,
      })
    }

    return { rhpaObserver, mediaObserver }
  }

  let observers: {rhpaObserver: MutationObserver, mediaObserver: MutationObserver } | null  = setupMutationObservers()

  // Keeps the RHPA in sync with the page top when a recipe thumbnail swap changes the visible configuration.
  function resetRhpaScroll() {
    if (isMobile()) return

    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0

    if (rhpaContent.value) {
      rhpaContent.value.style.top = `-${rhpaContent.value.clientHeight - window.innerHeight}px`
    }
    if (mediaContent.value) {
      mediaContent.value.style.top = ''
    }
    if (mediaContainer.value) {
      mediaContainer.value.style.height = ''
    }
  }

  function unmount() {
    // Add to unmount function:
    observers?.rhpaObserver?.disconnect()
    observers?.mediaObserver?.disconnect()
    observers = null

    window.removeEventListener('resize', handleResize)
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener)
    }

    // Clean up all styles
    if (mediaContent.value) {
      mediaContent.value.style.top = ''
      mediaContent.value.style.height = ''
    }
    if (mediaContainer.value) {
      mediaContainer.value.style.height = ''
      mediaContainer.value.style.top = ''
    }
  }

  return { unmount, resetRhpaScroll }
}
